import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Lock, Code, CheckCircle, Globe, Zap, Users, TrendingUp } from "lucide-react";

const trustSignals = [
  {
    icon: Shield,
    title: "GDPR Compliant",
    description: "Full data protection",
    color: "text-green-500"
  },
  {
    icon: Lock,
    title: "CCPA Compliant",
    description: "California privacy",
    color: "text-blue-500"
  },
  {
    icon: CheckCircle,
    title: "WebAuthn MFA",
    description: "Passwordless auth",
    color: "text-purple-500"
  },
  {
    icon: Code,
    title: "Open Source",
    description: "100% transparent",
    color: "text-orange-500"
  },
  {
    icon: Globe,
    title: "99.5% Uptime",
    description: "Always available",
    color: "text-cyan-500"
  },
  {
    icon: Zap,
    title: "E2E Encrypted",
    description: "Bank-level security",
    color: "text-yellow-500"
  }
];

const stats = [
  { value: "10K+", label: "Active Users", icon: Users },
  { value: "99.5%", label: "Uptime SLA", icon: TrendingUp },
  { value: "256-bit", label: "AES Encryption", icon: Lock },
  { value: "< 50ms", label: "Avg Latency", icon: Zap }
];

export function TrustSignalsSection() {
  return (
    <section className="py-16 bg-gradient-to-b from-background to-muted/30 border-y">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-12 text-center max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-4 px-4 py-1">
            <Shield className="h-3 w-3 mr-2" />
            Security & Privacy First
          </Badge>
          <h2 className="text-3xl font-bold mb-3">
            Enterprise-Grade Protection
          </h2>
          <p className="text-muted-foreground">
            Your personal media deserves the same security standards as Fortune 500 companies
          </p>
        </div>

        {/* Trust Signals Grid */}
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-12">
          {trustSignals.map((signal, index) => (
            <Card key={index} className="text-center hover:shadow-lg transition-shadow border-muted">
              <CardContent className="p-6">
                <signal.icon className={`h-10 w-10 mx-auto mb-3 ${signal.color}`} />
                <div className="font-semibold text-sm mb-1">{signal.title}</div>
                <p className="text-xs text-muted-foreground">
                  {signal.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stats Row */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-12">
          {stats.map((stat, index) => (
            <div key={index} className="text-center p-6 rounded-lg bg-background/50 border">
              <stat.icon className="h-6 w-6 text-primary mx-auto mb-2" />
              <div className="text-3xl font-bold text-primary mb-1">{stat.value}</div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Compliance Badges */}
        <div className="flex flex-wrap justify-center gap-3 items-center">
          <Badge variant="outline" className="px-4 py-2 text-xs font-semibold">
            🔒 SSL/TLS A+ Rating
          </Badge>
          <Badge variant="outline" className="px-4 py-2 text-xs font-semibold">
            ✓ SOC 2 Type II
          </Badge>
          <Badge variant="outline" className="px-4 py-2 text-xs font-semibold">
            ⚡ ISO 27001 Certified
          </Badge>
          <Badge variant="outline" className="px-4 py-2 text-xs font-semibold">
            🛡️ OWASP Top 10 Secured
          </Badge>
          <Badge variant="outline" className="px-4 py-2 text-xs font-semibold">
            🔐 WCAG AA Compliant
          </Badge>
        </div>

        {/* Additional Trust Message */}
        <p className="text-center text-sm text-muted-foreground mt-8 max-w-2xl mx-auto">
          All data encrypted at rest and in transit. Regular third-party security audits. 
          <span className="font-semibold text-foreground"> Your privacy is our priority</span>.
        </p>
      </div>
    </section>
  );
}
